import React, { useState, useEffect, useMemo } from "react";
import { Box, Alert, Typography } from "@mui/material";

import PeriodSelector from "../../components/PeriodSelector";
import GroupSelector from "../Metrics/components/GroupSelector";
import FiltersTopRow from "../../components/FiltersTopRow";
import FilterMenu from "../Metrics/components/FilterMenu";
import CostMetricsCards from "./components/CostMetricsCards";
import StockAlert from "./components/StockAlert";
import BannerPlacement, { BANNER_MAX_WIDTH } from "../../components/BannerPlacement";
import TopDishesTable from "./components/TopDishesTable";
import { CARD_LABEL_COLOR, CARD_SURFACE_SX, KPI_COLORS, dimSx } from "../../styles/tokens";
import { fetchCostAnalysisData } from "../../services/dataService";
import StickyFiltersBar from "../../components/StickyFiltersBar";
import { useDesktopLayout } from "../../components/layout/AppLayout";
import { useBusinessDay } from "../../hooks/useBusinessDay";
import { formatPeriodShort, getPeriodRange } from "../../utils/periods";
import { extractErrorMessage, isReportAccessDenied } from "../../utils/errors";
import NoReportAccess from "../../components/NoReportAccess";
import {
  MIN_ORDERS_FOR_COST_TOP,
  emptyTopCostReason,
  emptyTopProfitReason,
  calculateCostMetrics,
  hasSalesForSelection,
  getTopDishesByProfit,
  getTopDishesByCostPercent,
} from "./utils/costCalculations";
import { buildSelectionOptions } from "../../utils/restaurant";

/**
 * Вкладка «Обзор» раздела «Себестоимость» (CostPage). period / onPeriodChange — общий период вкладок,
 * tabs — переключатель «Обзор | ABC-анализ | Склад» для закреплённой панели. iikocloud — у сервера есть iikoCloud:
 * над карточками строка о минусах на складах (StockAlert).
 */
const CostOverviewTab = ({ selectedGroup: propSelectedGroup, allGroups: propAllGroups, activeServerName, setSelectedGroup: propSetSelectedGroup, period, onPeriodChange, tabs = null, iikocloud = false }) => {
  const desktop = useDesktopLayout();
  // Используем переданное значение или локальное (для обратной совместимости)
  const [localSelectedGroup, setLocalSelectedGroup] = useState(() => {
    const saved = localStorage.getItem("selectedGroup");
    return saved || "Все";
  });
  const selectedGroup = propSelectedGroup !== undefined ? propSelectedGroup : localSelectedGroup;
  const setSelectedGroup = propSetSelectedGroup || ((val) => {
    localStorage.setItem("selectedGroup", val);
    setLocalSelectedGroup(val);
  });
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [costData, setCostData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [topDishesByProfit, setTopDishesByProfit] = useState([]);
  const [topDishesByCostPercent, setTopDishesByCostPercent] = useState([]);
  const [localAllGroups, setLocalAllGroups] = useState(["Все"]);
  // Используем переданные группы или локальные (для обратной совместимости)
  const allGroups = propAllGroups || localAllGroups;
  const [dataLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false); // нет прав на отчёты iiko — мягкая карточка

  // Период — от учётного дня ресторана; пока он грузится, данные не запрашиваем (null)
  const businessDay = useBusinessDay();
  const dateRange = useMemo(() => (businessDay ? getPeriodRange(period, businessDay) : null), [period, businessDay]);
  const token = localStorage.getItem("token");
  const loading = dataLoading || !businessDay;
  const bannerLoading = loading;
  // Данных ещё нет — скелетоны; данные есть и идёт перезагрузка — старые остаются приглушёнными
  const skeleton = loading && !costData;
  const dimmed = loading && !skeleton;
  // У выбранного ресторана (группы) нет строк за период — «нет продаж» вместо карточек и топов
  const noSales = Boolean(costData) && !hasSalesForSelection(costData, selectedGroup);

  // 🔹 Загрузка данных с сервера
  useEffect(() => {
    if (!token || !dateRange?.from || !dateRange?.to) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        setAccessDenied(false);

        const cachedServer = localStorage.getItem("activeServer");
        const server = cachedServer ? JSON.parse(cachedServer) : null;
        if (!server) {
          setError("Сервер не найден");
          return;
        }

        const data = await fetchCostAnalysisData(token, server, dateRange);
        setCostData(data);

        // 🔹 Список ресторанов и групп в них (если страница открыта без общего выбора из шапки)
        if (data && Array.isArray(data.data)) {
          setLocalAllGroups(["Все", ...buildSelectionOptions(data.data)]);
        }
      } catch (err) {
        console.error("❌ Ошибка загрузки данных себестоимости:", err);
        if (isReportAccessDenied(err)) setAccessDenied(true);
        else setError(extractErrorMessage(err) || "Ошибка при загрузке данных");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [token, dateRange?.from, dateRange?.to]);

  // 🔹 Пересчет метрик и таблиц при изменении данных или фильтров
  useEffect(() => {
    if (!costData) {
      setMetrics(null);
      setTopDishesByProfit([]);
      setTopDishesByCostPercent([]);
      return;
    }

    const calculatedMetrics = calculateCostMetrics(costData, selectedGroup);
    setMetrics(calculatedMetrics);

    const topProfit = getTopDishesByProfit(costData, selectedGroup, 5);
    setTopDishesByProfit(topProfit);

    const topCostPercent = getTopDishesByCostPercent(costData, selectedGroup, 5);
    setTopDishesByCostPercent(topCostPercent);
  }, [costData, selectedGroup]);


  // Обработчики меню фильтров
  const handleOpenFilters = (e) => setFilterMenuAnchor(e.currentTarget);
  const handleCloseFilters = () => setFilterMenuAnchor(null);

  return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          color: "#fff",
          width: "100%",
        }}
      >
        {/* Ошибка */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              mb: 2,
              width: "100%",
              backgroundColor: "rgba(255, 50, 50, 0.1)",
              border: "1px solid rgba(255, 100, 100, 0.3)",
              color: "#fff",
              whiteSpace: "pre-line", // текст iiko бывает многострочным
            }}
          >
            {error}
          </Alert>
        )}

        <StickyFiltersBar>
          <FiltersTopRow>
            {/* Десктоп: выбор ресторана и вкладки — одной группой слева, на одной высоте */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, maxWidth: "100%" }}>
              {propAllGroups && (
                <GroupSelector
                  groups={propAllGroups}
                  selectedGroup={selectedGroup}
                  onSelectGroup={setSelectedGroup}
                  activeServerName={activeServerName}
                  sx={{ mb: 0 }}
                />
              )}
              {desktop && tabs}
            </Box>
          </FiltersTopRow>
          {!desktop && tabs}

          <PeriodSelector
            period={period}
            rangeLabel={formatPeriodShort(dateRange)}
            onChange={onPeriodChange}
            onOpenFilters={handleOpenFilters}
            showFilters={false}
          />
        </StickyFiltersBar>

        {/* Контейнер с баннером и карточками метрик */}
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: BANNER_MAX_WIDTH, md: "100%" },
            mt: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            alignItems: "center",
          }}
        >
          {/* Баннер для страницы себестоимости */}
          <BannerPlacement placement="cost_top_promo" token={token} loading={bannerLoading} />

          {/* Минусы на складах (iikoCloud) — себестоимость может быть посчитана неверно; от периода не зависит */}
          {iikocloud === true && <StockAlert selectedGroup={selectedGroup} allGroups={allGroups} />}

          {/* Карточки метрик; у выбранного ресторана нет продаж за период — так и пишем (не нули и не цифры сети) */}
          {accessDenied ? (
            <NoReportAccess />
          ) : noSales ? (
            <Box role="status" sx={{ ...CARD_SURFACE_SX, width: "100%", p: { xs: 2, md: 2.5 }, ...dimSx(dimmed) }}>
              <Typography sx={{ fontSize: "15px", color: CARD_LABEL_COLOR }}>
                {selectedGroup && selectedGroup !== "Все"
                  ? "За этот период нет продаж по выбранному ресторану"
                  : "За этот период нет продаж"}
              </Typography>
            </Box>
          ) : (
            <CostMetricsCards metrics={metrics} loading={skeleton} dimmed={dimmed} />
          )}
        </Box>

        {/* Таблицы топ-5 блюд (нет прав на отчёты или нет продаж — не показываем) */}
        {!accessDenied && !noSales && (
          <Box
            sx={{
              mt: 4,
              ...dimSx(dimmed),
              width: "100%",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              justifyContent: "center",
              // На десктопе карточки в ряду одной высоты
              alignItems: { xs: "center", md: "stretch" },
            }}
          >
            <TopDishesTable
              title="5 блюд с самой высокой валовой прибылью"
              data={topDishesByProfit}
              loading={skeleton}
              valueKey="profit"
              emptyText={costData ? emptyTopProfitReason(costData, selectedGroup) : undefined}
              barColor={KPI_COLORS.grossProfit}
              valueFormat={(val) => `${val.toLocaleString("ru-RU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ₽`}
            />
            <TopDishesTable
              title="5 блюд с самой высокой себестоимостью"
              subtitle={`Среди блюд с ${MIN_ORDERS_FOR_COST_TOP} и более заказами за период`}
              data={topDishesByCostPercent}
              loading={skeleton}
              valueKey="costPercent"
              emptyText={costData ? emptyTopCostReason(costData, selectedGroup) : undefined}
              barColor={KPI_COLORS.costPercent}
              valueFormat={(val) => `${val.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`}
            />
          </Box>
        )}

        {/* Меню фильтров */}
        <FilterMenu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={handleCloseFilters}
          payTypes={[]}
          groups={allGroups}
          selectedPayType="Все"
          selectedGroup={selectedGroup}
          onSelectPayType={() => {}}
          onSelectGroup={(val) => {
            setSelectedGroup(val);
            handleCloseFilters();
          }}
        />
      </Box>
  );
};

export default CostOverviewTab;

